import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  ToggleButton, ToggleButtonGroup, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { authFetch } from '../../utils/authHelpers';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';

const GAP_TYPE_LABELS = {
  missing_capability: { label: 'Ontbrekende functie', color: 'warning' },
  backend_error: { label: 'Backend fout', color: 'error' },
  tool_error: { label: 'Tool crash', color: 'error' },
  disambiguation_needed: { label: 'Doorvragen nodig', color: 'info' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}u geleden`;
  return `${Math.floor(hours / 24)}d geleden`;
}

export default function CapabilityGaps() {
  const [gaps, setGaps] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');

  const fetchGaps = useCallback(async () => {
    setLoading(true);
    try {
      const resolved = filter === 'resolved';
      const res = await authFetch(
        `${AI_ENGINE_URL}/api/v1/chat/assistant/capability-gaps?resolved=${resolved}&limit=100`
      );
      if (res.ok) {
        const data = await res.json();
        setGaps(data.gaps || []);
        setSummary(data.summary || {});
      }
    } catch (err) {
      console.error('Failed to fetch gaps:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchGaps(); }, [fetchGaps]);

  const handleResolve = async (id) => {
    try {
      const res = await authFetch(
        `${AI_ENGINE_URL}/api/v1/chat/assistant/capability-gaps/${id}/resolve`,
        { method: 'POST' }
      );
      if (res.ok) {
        toast.success('Gap opgelost');
        setGaps(prev => prev.filter(g => g.id !== id));
      }
    } catch (err) {
      toast.error('Markeren mislukt');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>AI Assistent — Capability Gaps</Typography>
          <Typography variant="body2" color="text.secondary">
            Automatisch gedetecteerde vragen die de assistent niet kon beantwoorden.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
            size="small"
          >
            <ToggleButton value="open">Open ({filter === 'open' ? gaps.length : '...'})</ToggleButton>
            <ToggleButton value="resolved">Opgelost</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={fetchGaps} size="small"><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Summary chips */}
      {filter === 'open' && Object.keys(summary).length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {Object.entries(summary).map(([type, count]) => {
            const meta = GAP_TYPE_LABELS[type] || { label: type, color: 'default' };
            return (
              <Chip
                key={type}
                label={`${meta.label}: ${count}`}
                color={meta.color}
                size="small"
                variant="outlined"
              />
            );
          })}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : gaps.length === 0 ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          {filter === 'open'
            ? 'Geen open gaps — de assistent kon alles beantwoorden!'
            : 'Geen opgeloste gaps gevonden.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Vraag</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 140 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 180 }}>Detail</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 100 }}>Wie</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 90 }}>Wanneer</TableCell>
                {filter === 'open' && <TableCell sx={{ width: 50 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {gaps.map((gap) => {
                const meta = GAP_TYPE_LABELS[gap.gap_type] || { label: gap.gap_type, color: 'default' };
                return (
                  <TableRow key={gap.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {gap.user_question || '—'}
                      </Typography>
                      {gap.assistant_response && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {gap.assistant_response}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={meta.label} color={meta.color} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {gap.tool_name && <><strong>{gap.tool_name}</strong><br /></>}
                        {gap.error_detail || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {gap.user_email?.split('@')[0] || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {timeAgo(gap.created_at)}
                      </Typography>
                    </TableCell>
                    {filter === 'open' && (
                      <TableCell>
                        <Tooltip title="Markeer als opgelost">
                          <IconButton size="small" color="success" onClick={() => handleResolve(gap.id)}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
